/**
 * @fileoverview Component for displaying events for a participant.
 * This component includes a table of events and a function for formatting the events.
 */

import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useParams } from 'react-router-dom'
import { useGetParticipantEventsQuery } from '../../../services/events'
import { formatOutput } from '../../../utils/formatOutput'


/**
 * @interface OutboundCallDetails
 * @description The details of an outbound call.
 * @property {string} call_id - The ID of the call.
 * @property {number} duration - The duration of the call in seconds.
 * @property {string} status - The status of the call.
 */
interface OutboundCallDetails extends Record<string, unknown> {
    call_id: string
    duration: number
    status: 'initiated' | 'ringing' | 'in-progress' | 'busy' | 'no-answer' | 'failed' | 'completed'
}

const columns = ['Event', 'Details', 'Date', 'User', 'Annotation']
const statusMap: Record<string, string> = {
    incomplete: 'Pending',
    selected: 'Selected',
    not_qualified: 'Lower priority',
    invited: 'Invited',
    review: 'Requiring review',
    excluded: 'Excluded',
    qualified: 'High priority',
    assigned: 'Pending',
    randomised: 'Randomised',
    complete: 'Ready',
    cancelled: 'Failed',
    expired: 'Failed',
}

/**
 * @function formatOutboundCallDetails
 * @description Formats the details of an outbound call.
 * @param {OutboundCallDetails} data - The details of the outbound call.
 * @returns {JSX.Element} - A React component that renders the details of the outbound call.
 */
function formatOutboundCallDetails(data: OutboundCallDetails): JSX.Element {
    if (['completed', 'ringing', 'in-progress'].includes(data.status)) {
        return <span className="text-gray-900">Duration: {formatOutput(data.duration)}s</span>
    }

    return <span className="text-gray-400">No answer</span>
}

/**
 * @function formatStageTransition
 * @description Formats the transition between two stages.
 * @param {string} from - The previous stage.
 * @param {string} to - The new stage.
 * @returns {JSX.Element} - A React component that renders the transition between the two stages.
 */
function formatStageTransition(from: string, to: string) {
    return (
        <span>
            <span className="text-gray-400">{from}</span>
            <span className="text-gray-500"> → </span>
            <span className="text-gray-900">{to}</span>
        </span>
    )
}

/**
 * @function formatID
 * @description Formats an ID with a prefix.
 * @param {string} prefix - The prefix to add to the ID.
 * @param {string} id - The ID to format.
 * @returns {JSX.Element} - A React component that renders the formatted ID.
 */
function formatID(prefix: string, id?: string) {
    if (!id) {
        return formatOutput(undefined)
    }
    return formatOutput(prefix + '–' + id.split('-')[0])
}

/**
 * @function formatAssignmentDetails
 * @description Formats the details of an assignment.
 * @param {string} name - The name of the assignment.
 * @param {string} id - The ID of the assignment.
 * @returns {JSX.Element} - A React component that renders the details of the assignment.
 */
function formatAssignmentDetails(name: string, id: string) {
    if (!id) {
        return formatOutput(name)
    }
    return (
        <>
            {formatOutput(name)}, {formatID('AID', id)}
        </>
    )
}

/**
 * @function mapStatusToStage
 * @description Maps a status to a stage.
 * @param {string} status - The status to map.
 * @returns {string} - The stage to map to.
 */
function mapStatusToStage(status: string) {
    if (!status) {
        return 'Requiring review'
    }

    return statusMap[status]
}

const ASSIGNMENT_STATUS = {
    assigned: 'Test assigned',
    incomplete: 'Test started',
    cancelled: 'Test failed: Cancelled',
    timeout: 'Test failed: Timeout',
    complete: 'Test completed',
}

/**
 * @function mapDataToEvent
 * @description Maps data to an event.
 * @param {Novoic.API.ParticipantEvent} data - The data to map.
 * @returns {Novoic.ParticipantEventListItem} - The event to map to.
 */
function mapDataToEvent({
    id,
    type,
    data,
    user,
    patient_id,
    created_at,
}: Novoic.API.ParticipantEvent): Novoic.ParticipantEventListItem {
    let event: string
    let details = formatOutput(undefined)
    switch (type) {
        case 'patient_status':
            event = 'Stage updated'
            details = formatStageTransition(
                mapStatusToStage(data.previous as string),
                mapStatusToStage(data.current as string)
            )
            break
        case 'patient_updated': {
            event = 'Participant updated'
            const field = Object.keys(data.fields)[0]
            const [old, current] = (data.fields as Record<string, string[]>)[field] as string[]
            details = (
                <>
                    {formatOutput(field)}: {formatStageTransition(old, current)}
                </>
            )
            break
        }
        case 'patient_site_matched':
            event = 'Site matched'
            details = formatOutput((data.site as Novoic.API.Site).name)
            break
        case 'patient_study_updated': {
            event = 'Study updated'
            const { from_study, to_study } = data as {
                from_study: Novoic.API.Study
                to_study: Novoic.API.Study
            }
            details = formatStageTransition(from_study.name, to_study.name)
            break
        }
        case 'patient_site_updated': {
            event = 'Site updated'
            const { from_site, to_site } = data as {
                from_site: Novoic.API.Site
                to_site: Novoic.API.Site
            }
            details = formatStageTransition(from_site.name, to_site.name)
            break
        }
        case 'request_phone':
            event = 'Phone number requested'
            break
        case 'outgoing_call':
            event = 'Outbound call'
            details = formatOutboundCallDetails(data as OutboundCallDetails)
            break
        case 'patient_study_assigned':
            event = 'Study assigned'
            details = formatOutput((data.study as Novoic.API.Study).name)
            break
        case 'patient_created':
            event = 'Participant created'
            details = formatID('PID', patient_id)
            break
        case 'assignment_invitation_link_shared_by_email':
            event = 'Test link shared via email'
            details = formatAssignmentDetails(
                data.assignment_name as string,
                data.assignment_id as string
            )
            break
        case 'assignment_invitation_link_shared_by_phone':
            event = 'Test link shared via phone'
            details = formatAssignmentDetails(
                data.assignment_name as string,
                data.assignment_id as string
            )
            break
        case 'patient_external_id_assigned':
            event = 'External ID assigned'
            details = (
                <>
                    {formatOutput((data.external as Record<string, string>).name)}:{' '}
                    {formatOutput((data.external as Record<string, string>).id)}
                </>
            )
            break
        case 'patient_external_id_removed':
            event = 'External ID removed'
            details = (
                <>
                    {formatOutput((data.external as Record<string, string>).name)}:{' '}
                    {formatOutput((data.external as Record<string, string>).id)}
                </>
            )
            break
        case 'patient_site_assigned':
            event = 'Site assigned'
            details = formatOutput((data.site as Novoic.API.Site).name)
            break
        case 'assignment_created':
            event = 'Test assigned'
            details = formatID('AID', data.assignment_id as string)
            break
        case 'assignment_status':
            event = ASSIGNMENT_STATUS[data.current as keyof typeof ASSIGNMENT_STATUS]
            details = formatID('AID', data.assignment_id as string)
            break
        default:
            event = type
    }

    return {
        id,
        event,
        details,
        annotation: data?.annotation as string,
        date: created_at,
        user: user?.name,
    }
}

/**
 * @function formatDate
 * @description Formats a date.
 * @param {string} date - The date to format.
 * @returns {JSX.Element} - A React component that renders the formatted date.
 */
function formatDate(date: string) {
    if (!date) {
        return formatOutput(date)
    }
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
        hour: 'numeric',
        minute: 'numeric',
    }
    return formatOutput(new Intl.DateTimeFormat('en-US', options).format(new Date(date)))
}

/**
 * @function EventsTab
 * @description The events tab component.
 * @returns {JSX.Element} - A React component that renders the events tab.
 */
export function EventsTab() {
    const { participantId } = useParams()
    const { data: events, isLoading } = useGetParticipantEventsQuery(participantId, {
        refetchOnMountOrArgChange: true,
    })

    if (isLoading) {
        return null
    }

    return (
        <table className="mb-24">
            <thead className="border-b">
                <tr>
                    {columns.map((col) => (
                        <th
                            key={col}
                            scope="col"
                            className="py-2.5 px-3 first:pl-8 text-left text-sm font-medium text-gray-900 whitespace-nowrap"
                        >
                            <button className="group inline-flex">
                                {col}
                                <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                            </button>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white border-b">
                {events.map(mapDataToEvent).map((event: Novoic.ParticipantEventListItem) => (
                    <tr key={event.id}>
                        <td
                            className="whitespace-nowrap pl-8 py-4 text-sm text-gray-900 font-medium"
                            role="presentation"
                        >
                            {formatOutput(event.event)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                            {event.details}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                            {formatDate(event.date)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                            {formatOutput(event.user)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-600 grow">
                            {formatOutput(event.annotation)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
